import { Form, Field, FieldGroup, FieldRelationship } from 'pbts/c1/api/form/v1/form'

export type RecordStringAny = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

export interface FieldConstraints {
    // Fields that are required together (field -> other fields it must be filled with)
    requiredTogether: Map<string, Set<string>>
    requiredTogetherGroups: string[][]
    // Field sets where at least one must be filled
    atLeastOne: string[][]
    // Mutually exclusive field sets (field -> other fields it's exclusive with)
    mutuallyExclusive: Map<string, Set<string>>
    mutuallyExclusiveGroups: string[][]
    // Dependency relationships: field -> fields it depends on
    dependsOn: Map<string, string[]>
    // Reverse dependency: field -> fields that depend on it
    dependedOnBy: Map<string, Set<string>>
}

export interface ParsedForm {
    fieldsByName: Map<string, Field>
    groups: FieldGroup[]
    defaultTabIndex: number
    constraints: FieldConstraints
}

/** Get fields for a group, resolving field names to Field objects */
export function getGroupFields(group: FieldGroup, fieldsByName: Map<string, Field>): Field[] {
    return group.fields.map((name) => fieldsByName.get(name)).filter((f): f is Field => f !== undefined)
}

/** Check if a value is considered "filled" for constraints. Falsy values (false, '', []) are "not filled". */
export function isValueFilled(value: unknown): boolean {
    if (value === undefined || value === null) return false
    if (value === '') return false
    if (value === false) return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
}

/**
 * Parse constraints from field relationships into lookup maps
 */
export function parseConstraints(relationships: FieldRelationship[]): FieldConstraints {
    const requiredTogether = new Map<string, Set<string>>()
    const requiredTogetherGroups: string[][] = []
    const atLeastOne: string[][] = []
    const mutuallyExclusive = new Map<string, Set<string>>()
    const mutuallyExclusiveGroups: string[][] = []
    const dependsOn = new Map<string, string[]>()
    const dependedOnBy = new Map<string, Set<string>>()

    for (const rel of relationships) {
        const fieldNames = rel.fieldNames

        switch (rel.kind.oneofKind) {
            case 'requiredTogether':
                requiredTogetherGroups.push([...fieldNames])
                for (const fieldName of fieldNames) {
                    const others = fieldNames.filter((n) => n !== fieldName)
                    const existing = requiredTogether.get(fieldName) ?? new Set()
                    others.forEach((n) => existing.add(n))
                    requiredTogether.set(fieldName, existing)
                }
                break

            case 'atLeastOne':
                atLeastOne.push([...fieldNames])
                break

            case 'mutuallyExclusive':
                mutuallyExclusiveGroups.push([...fieldNames])
                for (const fieldName of fieldNames) {
                    const others = fieldNames.filter((n) => n !== fieldName)
                    const existing = mutuallyExclusive.get(fieldName) ?? new Set()
                    others.forEach((n) => existing.add(n))
                    mutuallyExclusive.set(fieldName, existing)
                }
                break

            case 'dependentOn': {
                const deps = rel.kind.dependentOn.dependencyFieldNames
                for (const fieldName of fieldNames) {
                    const existing = dependsOn.get(fieldName) ?? []
                    dependsOn.set(fieldName, [...existing, ...deps])
                }
                for (const dep of deps) {
                    const existing = dependedOnBy.get(dep) ?? new Set()
                    fieldNames.forEach((n) => existing.add(n))
                    dependedOnBy.set(dep, existing)
                }
                break
            }

            default:
                break
        }
    }

    return {
        requiredTogether,
        requiredTogetherGroups,
        atLeastOne,
        mutuallyExclusive,
        mutuallyExclusiveGroups,
        dependsOn,
        dependedOnBy,
    }
}

/**
 * Parse a Form into a structured representation for efficient rendering
 */
export function parseFormStructure(form: Form): ParsedForm {
    // Build field lookup map
    const fieldsByName = new Map<string, Field>()
    for (const field of form.fields) {
        fieldsByName.set(field.name, field)
    }

    // Track which fields are assigned to groups
    const assignedFields = new Set<string>()
    for (const group of form.fieldGroups) {
        for (const fieldName of group.fields) {
            assignedFields.add(fieldName)
        }
    }

    // Build groups array: explicit groups with fields, then ungrouped fields
    const groups: FieldGroup[] = []

    for (const group of form.fieldGroups) {
        // Only include groups that have at least one existing field
        if (group.fields.some((name) => fieldsByName.has(name))) {
            groups.push(group)
        }
    }

    // Add synthetic group for ungrouped fields at the end
    const ungroupedFieldNames = form.fields.filter((f) => !assignedFields.has(f.name)).map((f) => f.name)
    if (ungroupedFieldNames.length > 0) {
        groups.push(FieldGroup.create({
            name: '__ungrouped__',
            displayName: form.displayName || 'General',
            fields: ungroupedFieldNames,
        }))
    }

    // Find default tab index
    const defaultIdx = groups.findIndex((g) => g.default)
    const defaultTabIndex = defaultIdx >= 0 ? defaultIdx : 0

    return {
        fieldsByName,
        groups,
        defaultTabIndex,
        constraints: parseConstraints(form.fieldRelationships),
    }
}

/** Check if a field should be disabled due to mutual exclusion (another exclusive field is filled) */
export function isFieldDisabledByMutualExclusion(
    fieldName: string,
    values: Record<string, unknown>,
    constraints: FieldConstraints,
): boolean {
    const exclusivePartners = constraints.mutuallyExclusive.get(fieldName)
    if (!exclusivePartners?.size) return false
    return Array.from(exclusivePartners).some((partner) => isValueFilled(values[partner]))
}

/** Check if a field is visible based on its DependentOn constraints */
export function isFieldVisibleByConstraints(
    fieldName: string,
    values: Record<string, unknown>,
    constraints: FieldConstraints,
): boolean {
    const dependencies = constraints.dependsOn.get(fieldName)
    if (!dependencies || dependencies.length === 0) {
        return true
    }

    // Field is visible only if all its dependencies are filled
    return dependencies.every((depName) => isValueFilled(values[depName]))
}

/** Build a map of field -> related fields to revalidate when it changes */
export function buildRevalidationMap(c: FieldConstraints): Map<string, string[]> {
    const result: Record<string, Set<string>> = {}
    const add = (from: string, to: string): void => {
        (result[from] ??= new Set()).add(to)
    }

    c.requiredTogether.forEach((partners, f) => partners.forEach((p) => add(f, p)))
    c.mutuallyExclusive.forEach((partners, f) => partners.forEach((p) => add(f, p)))
    c.dependedOnBy.forEach((dependents, dep) => dependents.forEach((d) => add(dep, d)))
    c.atLeastOne.forEach((g) => g.forEach((f) => g.forEach((o) => o !== f && add(f, o))))

    return new Map(Object.entries(result).map(([k, v]) => [k, [...v]]))
}
